import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import { LoadingStatusCode } from "../../../../lib/types/LoadingStatusCode";
import { AggregationRule } from "../../../../lib/types/AggregationRule";
import {
  getAggregationRulesRoute,
  getAggregationRulesVersionsRoute,
  getAggregationRuleVersionsRoute,
  getCloseAggregationRuleRoute,
  getCreateAggregationRuleRoute,
  getUpdateAggregationRuleRoute,
} from "./aggregationRulesRoutes";
import { catchApiError } from "../../../../lib/utils/catchApiError";
import { sortByToDate } from "../../../../lib/utils";

interface InitialState {
  aggregationRuleVersions: AggregationRule<"view">[];
  aggregationRuleVersionsLoading: LoadingStatusCode;
  aggregationRuleVersionsNotForViewing: AggregationRule<"view">[];
  aggregationRuleVersionsNotForViewingLoading: LoadingStatusCode;
  aggregationRules: AggregationRule<"view">[];
  aggregationRulesLoading: LoadingStatusCode;
  aggregationRule?: AggregationRule<"view">;
  aggregationRuleLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  aggregationRuleVersions: [],
  aggregationRuleVersionsLoading: "idle",
  aggregationRuleVersionsNotForViewing: [],
  aggregationRuleVersionsNotForViewingLoading: "idle",
  aggregationRules: [],
  aggregationRulesLoading: "idle",
  aggregationRuleLoading: "idle",
};

// Просмотр версий группы трафика
export const fetchAggregationRuleVersions = createAsyncThunk<AggregationRule<"view">[], number>(
  "aggregationRules/fetchAggregationRuleVersions",
  async (id) => {
    try {
      const response = axios.get<AggregationRule<"view">[]>(getAggregationRuleVersionsRoute(id));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий групп трафика. Попробуйте позже",
      });
    }
  }
);

// Получение группы трафика (для служебного использования, не для просмотра)
export const fetchAggregationRuleVersionsNotForViewing = createAsyncThunk<
  AggregationRule<"view">[],
  number
>("aggregationRules/fetchAggregationRuleVersionsNotForViewing", async (id) => {
  try {
    const response = axios.get<AggregationRule<"view">[]>(getAggregationRuleVersionsRoute(id));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки версий групп трафика. Попробуйте позже",
    });
  }
});

// Получение группы по id + дата
export const fetchAggregationRule = createAsyncThunk<
  AggregationRule<"view">,
  { id: number; onDate: string }
>("aggregationRules/fetchAggregationRule", async (params) => {
  try {
    const { data } = await axios.get<AggregationRule<"view">>(getAggregationRulesRoute(params));
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки группы трафика. Попробуйте позже",
    });
  }
});

// // Получение групп по списку id + дата
// export const fetchAggregationRules = createAsyncThunk<
//   AggregationRule<"view">[],
//   { id: number; onDate?: string }[]
// >("aggregationRules/fetchAggregationRules", async (params) => {
//   try {
//     const response = Promise.all(
//       params.map((item) =>
//         axios.get<AggregationRule<"view">>(getAggregationRulesRoute(item))
//       )
//     );
//     const result = (await response).map(({ data }) => data);
//     return result;
//   } catch (e) {
//     return catchApiError({
//       e,
//       title: "Ошибка загрузки групп трафика. Попробуйте позже",
//     });
//   }
// });

// Получение групп по списку id из метода получения версий, попадающих в заданный диапазон дат (берется последняя версия)
// Пример использования: получить список групп, относящихся к конкретной версии предложения
export const fetchAggregationRulesFromVersions = createAsyncThunk<
  AggregationRule<"view">[],
  { ids: number[]; fromDate: string; toDate: string }
>("aggregationRules/fetchAggregationRulesFromVersions", async ({ ids, fromDate, toDate }) => {
  try {
    const { data } = await axios.get<AggregationRule<"view">[]>(
      getAggregationRulesVersionsRoute(ids)
    );
    data.sort((...args) => sortByToDate(...args, "DESC"));
    const result: AggregationRule<"view">[] = [];
    ids.forEach((id) => {
      const datesSatisfyingVersion = data.find((version) => {
        if (version.id === id) {
          // Попадание version.fromDate в заданный диапазон
          const isSatisfyingFromDate = moment(version.fromDate).isBetween(
            fromDate,
            toDate,
            "day",
            "[]"
          );
          // Попадание version.toDate в заданный диапазон
          const isSatisfyingToDate = moment(version.toDate).isBetween(
            fromDate,
            toDate,
            "day",
            "[]"
          );
          // Попадание заданного fromDate в диапазон дат версии
          const isSatisfyingVersionFromDate = moment(fromDate).isBetween(
            version.fromDate,
            version.toDate,
            "day",
            "[]"
          );
          // Попадание заданного toDate в диапазон дат версии
          const isSatisfyingVersionToDate = moment(toDate).isBetween(
            version.fromDate,
            version.toDate,
            "day",
            "[]"
          );
          return (
            isSatisfyingFromDate ||
            isSatisfyingToDate ||
            isSatisfyingVersionFromDate ||
            isSatisfyingVersionToDate
          );
        }
      });
      if (datesSatisfyingVersion) {
        result.push(datesSatisfyingVersion);
      }
    });
    return [...result].sort((a, b) => (a.id > b.id ? 1 : -1));
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки групп трафика. Попробуйте позже",
    });
  }
});

// Создание групп трафика
export const createAggregationRules = createAsyncThunk<
  AggregationRule<"view">[],
  AggregationRule<"createObj">[]
>("aggregationRules/createAggregationRules", async (params) => {
  try {
    const response = Promise.all(
      params.map((item) =>
        axios.post<AggregationRule<"view">>(getCreateAggregationRuleRoute(), item)
      )
    );
    const result = (await response).map(({ data }) => data);
    return result;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка создания группы трафика. Попробуйте позже",
    });
  }
});

// Обновление группы трафика
export const updateAggregationRule = createAsyncThunk<
  AggregationRule<"view">,
  {
    aggregationRule: AggregationRule<"createObj">;
    fromDate?: string;
    force?: boolean;
    ruleId: number;
  }
>(
  "aggregationRules/updateAggregationRule",
  async ({ ruleId, aggregationRule, force, fromDate }) => {
    try {
      const { data } = await axios.patch<AggregationRule<"view">>(
        getUpdateAggregationRuleRoute({ ruleId, fromDate, force }),
        aggregationRule
      );
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка обновления группы трафика. Попробуйте позже",
      });
    }
  }
);

// Изменение даты закрытия группы трафика
export const closeAggregationRule = createAsyncThunk<
  any,
  {
    fromDate: string;
    ruleId: number;
  }
>("aggregationRules/closeAggregationRule", async ({ ruleId, fromDate }) => {
  try {
    const { data } = await axios.delete<any>(getCloseAggregationRuleRoute({ ruleId, fromDate }));
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка измненения даты закрытия группы трафика. Попробуйте позже",
    });
  }
});

export const aggregationRulesSlice = createSlice({
  name: "aggregationRules",
  initialState,
  reducers: {
    cleanAggregationRuleVersions: (state) => {
      state.aggregationRuleVersionsLoading = "idle";
      state.aggregationRuleVersions = [];
    },
    cleanAggregationRuleVersionsNotForViewing: (state) => {
      state.aggregationRuleVersionsNotForViewingLoading = "idle";
      state.aggregationRuleVersionsNotForViewing = [];
    },
    cleanAggregationRules: (state) => {
      state.aggregationRulesLoading = "idle";
      state.aggregationRules = [];
    },
    cleanAggregationRule: (state) => {
      state.aggregationRuleLoading = "idle";
      state.aggregationRule = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAggregationRuleVersions.pending, (state) => {
        state.aggregationRuleVersionsLoading = "pending";
      })
      .addCase(fetchAggregationRuleVersions.rejected, (state) => {
        state.aggregationRuleVersionsLoading = "rejected";
        state.aggregationRuleVersions = [];
      })
      .addCase(fetchAggregationRuleVersions.fulfilled, (state, { payload }) => {
        state.aggregationRuleVersionsLoading = "fulfilled";
        state.aggregationRuleVersions = [...payload].sort(sortByToDate);
      })
      .addCase(fetchAggregationRuleVersionsNotForViewing.pending, (state) => {
        state.aggregationRuleVersionsNotForViewingLoading = "pending";
      })
      .addCase(fetchAggregationRuleVersionsNotForViewing.rejected, (state) => {
        state.aggregationRuleVersionsNotForViewingLoading = "rejected";
        state.aggregationRuleVersionsNotForViewing = [];
      })
      .addCase(fetchAggregationRuleVersionsNotForViewing.fulfilled, (state, { payload }) => {
        state.aggregationRuleVersionsNotForViewingLoading = "fulfilled";
        state.aggregationRuleVersionsNotForViewing = payload;
      })
      // .addCase(fetchAggregationRules.pending, (state) => {
      //   state.aggregationRulesLoading = "pending";
      // })
      // .addCase(fetchAggregationRules.rejected, (state) => {
      //   state.aggregationRulesLoading = "rejected";
      //   state.aggregationRules = [];
      // })
      // .addCase(fetchAggregationRules.fulfilled, (state, { payload }) => {
      //   state.aggregationRulesLoading = "fulfilled";
      //   state.aggregationRules = payload;
      // })
      .addCase(fetchAggregationRulesFromVersions.pending, (state) => {
        state.aggregationRulesLoading = "pending";
      })
      .addCase(fetchAggregationRulesFromVersions.rejected, (state) => {
        state.aggregationRulesLoading = "rejected";
        state.aggregationRules = [];
      })
      .addCase(fetchAggregationRulesFromVersions.fulfilled, (state, { payload }) => {
        state.aggregationRulesLoading = "fulfilled";
        state.aggregationRules = payload;
      })
      .addCase(createAggregationRules.pending, (state) => {
        state.aggregationRuleLoading = "pending";
      })
      .addCase(createAggregationRules.rejected, (state) => {
        state.aggregationRuleLoading = "rejected";
      })
      .addCase(createAggregationRules.fulfilled, (state) => {
        state.aggregationRuleLoading = "fulfilled";
      })
      .addCase(closeAggregationRule.pending, (state) => {
        state.aggregationRuleLoading = "pending";
      })
      .addCase(closeAggregationRule.rejected, (state) => {
        state.aggregationRuleLoading = "rejected";
      })
      .addCase(closeAggregationRule.fulfilled, (state) => {
        state.aggregationRuleLoading = "fulfilled";
      })
      .addCase(fetchAggregationRule.pending, (state) => {
        state.aggregationRuleLoading = "pending";
      })
      .addCase(fetchAggregationRule.rejected, (state) => {
        state.aggregationRuleLoading = "rejected";
        state.aggregationRule = undefined;
      })
      .addCase(fetchAggregationRule.fulfilled, (state, { payload }) => {
        state.aggregationRuleLoading = "fulfilled";
        state.aggregationRule = payload;
      })
      .addCase(updateAggregationRule.pending, (state) => {
        state.aggregationRuleLoading = "pending";
      })
      .addCase(updateAggregationRule.rejected, (state) => {
        state.aggregationRuleLoading = "rejected";
      })
      .addCase(updateAggregationRule.fulfilled, (state) => {
        state.aggregationRuleLoading = "fulfilled";
      });
  },
});

export const {
  reducer: aggregationRulesReducer,
  actions: aggregationRulesActions,
} = aggregationRulesSlice;
